'use es6';

import ReactDOM from 'react-dom';
import PortalIdParser from 'PortalIdParser';
import formatName from 'I18n/utils/formatName';
import I18n from 'I18n';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { getFullUrl } from 'hubspot-url-utils';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import styled from 'academy-common-lib/utils/Styling';
import useWaitUntilQuerySelector from '../hooks/useWaitUntilQuerySelector';
import UsageTracker from '../utils/UsageTracker';
import { useHomepageUrl } from '../features/navigation';
import { useIsPaidAccount } from '../hooks/productsHooks';
import { useHasScope } from '../features/accounts';
import { jsx as _jsx } from "react/jsx-runtime";
const RestyledNavElementsContainer = styled.div.withConfig({
  displayName: "StolenFaceNav__RestyledNavElementsContainer",
  componentId: "sc-1avad2s-0"
})(["display:block !important;#navbar{height:48px !important;}.accountexpansion{top:58px !important;}"]);
const StolenFaceNav = () => {
  const relocationTarget = useRef();
  const faceNavRoot = useWaitUntilQuerySelector('#account-menu-container');
  const accountName = useWaitUntilQuerySelector('.account-name');
  const academyHyperlink = useWaitUntilQuerySelector('a#academy');
  const accountsExtra = document.querySelector('ul.account-extras');
  const {
    hasScope
  } = useHasScope('academy-studio-content-management');
  const {
    homepage: homepageUrl
  } = useHomepageUrl();
  const [isPaidAccount] = useIsPaidAccount();
  const portalId = PortalIdParser.getPortalIdFromPath();
  const isPaidWithHomepage = isPaidAccount && homepageUrl;
  const user = useSelector(state => state.auth.user);

  // relocate face nav
  useLayoutEffect(() => {
    if (faceNavRoot) {
      const parent = faceNavRoot.parentElement;
      const target = relocationTarget.current;
      target.setAttribute('data-test-id', 'stolen-face-nav-target');
      parent.removeChild(faceNavRoot);
      target.appendChild(faceNavRoot);
      return () => {
        target.removeChild(faceNavRoot);
        parent.appendChild(faceNavRoot);
      };
    }
    return () => {};
  }, [faceNavRoot]);

  // portal name -> user name
  useLayoutEffect(() => {
    if (accountName && user) {
      const oldName = accountName.innerText;
      accountName.innerText = formatName({
        firstName: user.firstName,
        lastName: user.lastName
      });
      return () => {
        accountName.innerText = oldName;
      };
    }
    return () => {};
  }, [accountName, user]);

  // HubSpot Academy -> HubSpot studio menu option
  useLayoutEffect(() => {
    const academyToStudio = document.createElement('li');
    if (accountsExtra) {
      const trackClick = () => {
        UsageTracker.track('interaction', {
          action: 'stolen-face-nav-hubspot-studio-click'
        });
      };
      if (hasScope) {
        const isLocal = /^local/.test(document.location.hostname);
        const url = `${getFullUrl(isLocal ? 'local' : 'app')}/academy-studio/${portalId}`;
        const link = /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: url,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyNav.academyStudio"
          })
        });
        academyToStudio.addEventListener('click', trackClick);
        ReactDOM.render(link, academyToStudio);
        accountsExtra.appendChild(academyToStudio);
      }
      return () => {
        academyToStudio.removeEventListener('click', trackClick);
      };
    }
    return () => {};
  }, [accountsExtra, portalId, hasScope]);

  // HubSpot Academy -> HubSpot Software menu option
  useLayoutEffect(() => {
    if (academyHyperlink) {
      const oldText = academyHyperlink.innerText;
      const oldHref = academyHyperlink.href;
      const oldTarget = academyHyperlink.target;
      academyHyperlink.innerText = I18n.text('academyNav.softwareDesirability.inlineLink.enrolled');
      academyHyperlink.target = '_blank';
      if (isPaidWithHomepage) {
        academyHyperlink.href = homepageUrl;
      } else {
        academyHyperlink.href = `/user-guide/${portalId}`;
      }
      const trackClick = () => {
        UsageTracker.track('interaction', {
          action: 'stolen-face-nav-hubspot-software-click',
          to: isPaidWithHomepage ? 'homepage' : 'user-guide'
        });
      };
      const iconSpan = document.createElement('span');
      iconSpan.id = 'face-nav-external-link-icon';
      academyHyperlink.appendChild(iconSpan);
      ReactDOM.render( /*#__PURE__*/_jsx(UIIcon, {
        className: "m-left-1",
        name: "externalLink"
      }), iconSpan);
      academyHyperlink.addEventListener('click', trackClick);
      return () => {
        academyHyperlink.innerText = oldText;
        academyHyperlink.href = oldHref;
        academyHyperlink.target = oldTarget;
        academyHyperlink.childNodes.forEach(childNode => {
          if (childNode.id === 'face-nav-external-link-icon') {
            academyHyperlink.removeChild(childNode);
          }
        });
        academyHyperlink.removeEventListener('click', trackClick);
      };
    }
    return () => {};
  }, [academyHyperlink, homepageUrl, isPaidWithHomepage, portalId]);

  // Top level styling
  useLayoutEffect(() => {
    const id = 'stolen-face-nav-styles-adjustments';
    const styleEl = document.createElement('style');
    styleEl.id = id;
    document.getElementsByTagName('head')[0].appendChild(styleEl);
    styleEl.innerHTML = `
      #hs-nav-v4 .arrow-down-icon {
        margin-top: -5px;
      }

      #hs-nav-v4 #account-menu {
        height: 48px !important;
      }
    `;
    return () => {
      const foundEl = document.getElementById(id);
      if (foundEl) {
        foundEl.parentElement.removeChild(foundEl);
      }
    };
  }, []);
  const handleFaceNavClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'stolen-face-nav-click'
    });
  }, []);
  return /*#__PURE__*/_jsx(RestyledNavElementsContainer, {
    "data-test-id": "stolen-face-nav",
    id: "hs-nav-v4",
    onClick: handleFaceNavClick,
    style: {
      position: 'relative',
      top: 0
    },
    children: /*#__PURE__*/_jsx("div", {
      id: "navbar",
      ref: relocationTarget
    })
  });
};
export default StolenFaceNav;